import { useEffect, useRef } from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import { SpotlightRect } from '../../hooks/useTutorial';
import { TutorialStep } from '../../tutorial/tours';

/** Breathing room between the spotlit element and the dimmed area. */
const PADDING_PX = 6;
/** Above the AppBar (1100) and MUI dialogs (1300) so nothing overlaps it. */
const Z_INDEX = 1500;
const CAPTION_WIDTH_PX = 320;
/** Enough for a title, two short lines and the button row. */
const CAPTION_HEIGHT_PX = 190;
const GAP_PX = 12;

const SCRIM = 'rgba(0, 0, 0, 0.62)';

const prefersReducedMotion = (): boolean =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Elements that can take focus themselves, so the step is keyboard-operable. */
const FOCUSABLE = 'a[href], button, input, select, textarea, [tabindex]';

interface TutorialOverlayProps {
  title: string;
  step: TutorialStep;
  position: number;
  total: number;
  rect: SpotlightRect;
  /** The spotlit element — focused when it can take focus. */
  target: Element | null;
  onSkip: () => void;
}

/**
 * The spotlight (issue #427): four panels dim everything except the step's
 * target, which is left uncovered and fully interactive. The panels swallow
 * clicks, so the only thing the operator can act on is the highlighted
 * element — that click is what advances the tutorial (see useTutorial).
 */
export const TutorialOverlay: React.FC<TutorialOverlayProps> = ({
  title,
  step,
  position,
  total,
  rect,
  target,
  onSkip,
}) => {
  const captionRef = useRef<HTMLDivElement | null>(null);

  // Keyboard operators need focus on the thing they are being asked to press;
  // when the target cannot take focus (the map), park it on the caption so the
  // step is still announced and Skip is one Tab away.
  useEffect(() => {
    if (target instanceof HTMLElement && target.matches(FOCUSABLE)) {
      target.focus();
      return;
    }
    captionRef.current?.focus();
  }, [step, target]);

  const hole = {
    top: rect.top - PADDING_PX,
    left: rect.left - PADDING_PX,
    width: rect.width + PADDING_PX * 2,
    height: rect.height + PADDING_PX * 2,
  };
  const bottom = hole.top + hole.height;
  const right = hole.left + hole.width;

  // Below the target when it fits, otherwise above it; clamped to the viewport.
  const fitsBelow = bottom + GAP_PX + CAPTION_HEIGHT_PX < window.innerHeight;
  const captionTop = fitsBelow
    ? bottom + GAP_PX
    : Math.max(GAP_PX, hole.top - GAP_PX - CAPTION_HEIGHT_PX);
  const captionLeft = Math.min(
    Math.max(GAP_PX, hole.left),
    Math.max(GAP_PX, window.innerWidth - CAPTION_WIDTH_PX - GAP_PX)
  );

  // Geometry changes on every measurement, so it lives in inline styles rather
  // than `sx` — no freshly emitted CSS class per frame, and it stays readable
  // straight off the element.
  const scrimSx = {
    position: 'fixed' as const,
    bgcolor: SCRIM,
    zIndex: Z_INDEX,
  };
  // Clicks on the dimmed area are absorbed here: they neither reach the app
  // nor advance the tutorial.
  const swallow = (e: React.SyntheticEvent) => e.preventDefault();
  const motion = prefersReducedMotion() ? 'none' : 'all 150ms ease-out';

  return (
    <>
      <Box
        sx={scrimSx}
        style={{ top: 0, left: 0, right: 0, height: Math.max(0, hole.top) }}
        onMouseDown={swallow}
        onClick={swallow}
        data-testid="tutorial-scrim-top"
      />
      <Box
        sx={scrimSx}
        style={{ top: bottom, left: 0, right: 0, bottom: 0 }}
        onMouseDown={swallow}
        onClick={swallow}
        data-testid="tutorial-scrim-bottom"
      />
      <Box
        sx={scrimSx}
        style={{
          top: hole.top,
          left: 0,
          width: Math.max(0, hole.left),
          height: hole.height,
        }}
        onMouseDown={swallow}
        onClick={swallow}
        data-testid="tutorial-scrim-left"
      />
      <Box
        sx={scrimSx}
        style={{ top: hole.top, left: right, right: 0, height: hole.height }}
        onMouseDown={swallow}
        onClick={swallow}
        data-testid="tutorial-scrim-right"
      />

      {/* Ring around the spotlit element — decoration only, never in the way. */}
      <Box
        aria-hidden
        data-testid="tutorial-spotlight"
        style={{
          top: hole.top,
          left: hole.left,
          width: hole.width,
          height: hole.height,
        }}
        sx={{
          position: 'fixed',
          border: '2px solid',
          borderColor: 'secondary.main',
          borderRadius: 1,
          pointerEvents: 'none',
          zIndex: Z_INDEX + 1,
          transition: motion,
        }}
      />

      <Paper
        elevation={8}
        ref={captionRef}
        tabIndex={-1}
        role="dialog"
        aria-modal={false}
        aria-labelledby="tutorial-caption-title"
        aria-live="polite"
        style={{ top: captionTop, left: captionLeft }}
        sx={{
          position: 'fixed',
          width: CAPTION_WIDTH_PX,
          maxWidth: `calc(100vw - ${GAP_PX * 2}px)`,
          p: 2,
          zIndex: Z_INDEX + 2,
          transition: motion,
        }}
      >
        <Typography
          id="tutorial-caption-title"
          variant="subtitle2"
          component="h2"
          gutterBottom
        >
          {title}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {step.action}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {step.outcome}
        </Typography>
        <Box
          sx={{
            mt: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Step {position} of {total}
          </Typography>
          <Button size="small" onClick={onSkip}>
            Skip tutorial
          </Button>
        </Box>
      </Paper>
    </>
  );
};
